/* istanbul ignore file */

import React, { FunctionComponent, MutableRefObject, useRef } from 'react';
import { CurrencySymbol } from '@noths/polaris-dev-ts-types';
import * as R from 'ramda';

import { RangeInput, RangeInputValues } from 'src/components/molecules/RangeInput';
import { FilterUid } from 'src/components/organisms/Filter/constants/uids';
import { Filter, FilterOptionValue, SelectedFilterOption } from 'src/types';
import { FilterOptionsProps } from '../../../types';
import { getRangeInputProperties } from './getRangeInputProperties';
import { getRangeInputValues } from './getRangeInputValues';

type PrevFilterUpdateRef = (SelectedFilterOption | undefined)[];

export const onChange = (
  newValues: RangeInputValues,
  filter: Filter,
  prevFilterUpdateRef: MutableRefObject<PrevFilterUpdateRef>,
  onSelectFilterOption: FilterOptionsProps['onSelectFilterOption'],
) =>
  newValues.forEach((value: FilterOptionValue, index) => {
    const currentFilterOptionValue =
      Array.isArray(prevFilterUpdateRef.current) && prevFilterUpdateRef.current.length >= index
        ? prevFilterUpdateRef.current[index]
        : null;

    const newFilterOptionValue = {
      filterUid: filter.uid,
      filterOptionUid: filter.options[index].uid,
      filterOptionValue: value,
    } as SelectedFilterOption;

    if (!R.equals(currentFilterOptionValue, newFilterOptionValue)) {
      prevFilterUpdateRef.current[index] = newFilterOptionValue;
      onSelectFilterOption(newFilterOptionValue);
    }
  });

export const RangeInputWrapper: FunctionComponent<FilterOptionsProps> = ({
  currencyCode,
  filter,
  selectedFilterOptions,
  onSelectFilterOption,
}: FilterOptionsProps) => {
  const { step, range, handleLabels, handleInputNames } = getRangeInputProperties(filter);
  const values = getRangeInputValues(selectedFilterOptions, filter.uid, range, filter.options);
  const valueTextPrefix = filter.uid === FilterUid.Price ? CurrencySymbol[currencyCode] : undefined;

  // (Ref used to avoid additional re-renders on top of prop changes caused by Redux)
  const prevFilterUpdateRef = useRef<PrevFilterUpdateRef>([
    {
      filterUid: filter.uid,
      filterOptionUid: filter.options[0].uid,
      filterOptionValue: values[0],
    },
    {
      filterUid: filter.uid,
      filterOptionUid: filter.options[1].uid,
      filterOptionValue: values[1],
    },
  ]);

  return (
    <RangeInput
      values={values}
      range={range}
      step={step}
      accessibleHandleLabels={handleLabels}
      handleInputNames={handleInputNames}
      valueTextPrefix={valueTextPrefix}
      onChange={(newValues) =>
        onChange(newValues, filter, prevFilterUpdateRef, onSelectFilterOption)
      }
    />
  );
};
